import React, { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { sanitizeText } from '../lib/sanitize';
import { parseVideoUrl } from '../lib/cardStyle';

// Ícone de "play" preenchido (o Play do lucide é contornado).
function PlayIconSolid() {
  return (
    <svg viewBox="0 0 24 24" fill="#1a1410" className="w-6 h-6 translate-x-[1px]" aria-hidden="true">
      <path d="M8 5.5v13l11-6.5-11-6.5Z" />
    </svg>
  );
}

// Bloco de vídeo: thumbnail → embed on-click (YouTube nocookie / Vimeo).
// Reutilizável.
// config: { url, heading:{eyebrow,title} (rótulo de seção ACIMA do vídeo),
//           title (título do vídeo, ABAIXO), subtitle (meta), showOpenLink }
// btnStyle: { bg, text } já resolvido (usa a cor/degradê do botão do cartão).
export default function BlockVideo({ config, onTrack, btnStyle }) {
  const { url, heading = {}, title, subtitle, showOpenLink } = config;
  const [playing, setPlaying] = useState(false);
  const [thumbBroken, setThumbBroken] = useState(false);
  const v = parseVideoUrl(url);
  if (!v) return null;

  const playBg = (btnStyle && btnStyle.bg) || 'var(--color-action-dark)';
  const hasHeading = heading && (heading.eyebrow || heading.title);

  return (
    <div>
      {hasHeading && (
        <div className="mb-3">
          {heading.eyebrow && (
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-action dark:text-action-dark">
              {sanitizeText(heading.eyebrow)}
            </p>
          )}
          {heading.title && (
            <h3 className="text-xl text-text-primary dark:text-text-primary-dark mt-1" style={{ fontFamily: 'var(--font-display, inherit)' }}>
              {sanitizeText(heading.title)}
            </h3>
          )}
        </div>
      )}
      <div className="rounded-input overflow-hidden border border-border dark:border-border-dark bg-card dark:bg-card-dark">
        <div className="relative w-full bg-surface dark:bg-surface-dark" style={{ aspectRatio: '16 / 9' }}>
          {playing ? (
            <iframe
              className="absolute inset-0 w-full h-full"
              src={v.embedUrl}
              title={sanitizeText(heading.title || 'Vídeo')}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <button
              type="button"
              onClick={() => { setPlaying(true); onTrack && onTrack('video_play', { provider: v.provider, id: v.id }); }}
              className="group absolute inset-0 w-full h-full"
              aria-label={`Tocar ${sanitizeText(heading.title || 'vídeo')}`}
            >
              {(v.thumbUrlHi || v.thumbUrl) ? (
                <img
                  src={thumbBroken ? v.thumbUrl : (v.thumbUrlHi || v.thumbUrl)}
                  onError={() => setThumbBroken(true)}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-surface dark:bg-surface-dark" />
              )}
              <span className="absolute inset-0 grid place-items-center bg-black/25">
                <span className="grid h-16 w-16 place-items-center rounded-full shadow-lg transition-transform group-active:scale-95" style={{ background: playBg }}>
                  <PlayIconSolid />
                </span>
              </span>
            </button>
          )}
        </div>
        {(title || subtitle || (showOpenLink && v.watchUrl)) && (
          <div className="flex items-center justify-between gap-3 p-4">
            <div className="min-w-0 flex-1">
              {title && (
                <p className="font-semibold text-text-primary dark:text-text-primary-dark leading-snug">
                  {sanitizeText(title)}
                </p>
              )}
              {subtitle && (
                <p className="text-sm text-text-muted dark:text-text-muted-dark leading-snug mt-0.5">
                  {sanitizeText(subtitle)}
                </p>
              )}
            </div>
            {showOpenLink && (
              <a
                href={v.watchUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() => onTrack && onTrack('video_open', { provider: v.provider, id: v.id })}
                className="shrink-0 self-center rounded-full border border-border dark:border-border-dark p-2.5 text-action dark:text-action-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors"
                aria-label="Abrir no site do vídeo"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
